'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface Props {
  orderId: number
  onClose: () => void
  children: ReactNode
}

export function OrderDetailDrawer({ orderId, onClose, children }: Props) {
  const closeButton = useRef<HTMLButtonElement | null>(null)
  const panel = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panel.current) return

      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || !panel.current.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    requestAnimationFrame(() => closeButton.current?.focus())
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      previousFocus?.focus()
    }
  }, [onClose])

  return (
    <div className="absolute inset-0 z-40" role="dialog" aria-modal="true" aria-label={`Заказ №${orderId}`}>
      <button
        type="button"
        onClick={onClose}
        className="mission-drawer-backdrop absolute inset-0 bg-onyx/24 backdrop-blur-[1px]"
        aria-label="Закрыть заказ"
      />
      <aside ref={panel} className="mission-drawer-enter absolute inset-y-0 right-0 w-full overflow-y-auto bg-ivory shadow-[-24px_0_70px_rgba(20,17,13,0.16)] md:w-[min(1120px,94%)] xl:w-[min(1180px,92%)]">
        <div className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-rule bg-ivory/94 px-4 backdrop-blur md:px-7">
          <div className="min-w-0">
            <div className="eyebrow">Карточка заказа</div>
            <div className="mt-0.5 truncate text-[13px] font-semibold tab-nums text-ink">Заказ №{orderId}</div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/orders?orderId=${orderId}`}
              className="hidden h-9 items-center rounded-md px-3 text-[11px] font-medium text-ink-muted transition hover:bg-bone hover:text-ink sm:inline-flex"
            >
              Открыть в «Заказах» →
            </Link>
            <button
              ref={closeButton}
              type="button"
              onClick={onClose}
              className="size-10 rounded-full text-[24px] leading-none text-ink-muted transition hover:bg-bone hover:text-ink"
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-[1120px] px-4 py-5 pb-[calc(28px+env(safe-area-inset-bottom))] md:px-8 md:py-7">
          {children}
        </div>
      </aside>
    </div>
  )
}
